import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { useTheme } from '../../contexts/ThemeContext';

export type MarkdownSize = 'small' | 'medium' | 'large';

interface PersonaMarkdownProps {
  content: string;
  size?: MarkdownSize;
  className?: string;
  maxHeight?: string;
}

export const PersonaMarkdown: React.FC<PersonaMarkdownProps> = ({
  content,
  size = 'medium',
  className = '',
  maxHeight
}) => {
  const { darkMode } = useTheme();
  
  // Size-based classes
  const sizeClasses = {
    small: 'text-xs prose-xs',
    medium: 'text-sm prose-sm',
    large: 'text-base prose-base'
  };
  
  // Create components with proper styling
  const components = {
    // Headings with proper hierarchy and spacing
    h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
    h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-5 mb-3" {...props} />,
    h3: ({node, ...props}) => <h3 className="text-lg font-bold mt-4 mb-2" {...props} />,
    h4: ({node, ...props}) => <h4 className="text-base font-bold mt-3 mb-2" {...props} />,
    h5: ({node, ...props}) => <h5 className="text-sm font-bold mt-2 mb-1" {...props} />,
    h6: ({node, ...props}) => <h6 className="text-xs font-bold mt-2 mb-1" {...props} />,
    
    // Paragraphs with proper spacing
    p: ({node, ...props}) => <p className="mb-4 leading-relaxed" {...props} />,
    
    // Lists with proper indentation and spacing
    ul: ({node, ordered, ...props}) => <ul className="list-disc pl-6 mb-4" {...props} />,
    ol: ({node, ordered, ...props}) => <ol className="list-decimal pl-6 mb-4" {...props} />,
    li: ({node, ordered, checked, ...props}) => <li className="mb-2" {...props} />,
    
    // Code blocks with better styling
    code: ({node, inline, className, children, ...props}) => {
      return inline ? (
        <code className={`px-1 py-0.5 rounded font-mono text-sm ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-200 text-gray-800'} ${className}`} {...props}>
          {children}
        </code>
      ) : (
        <pre className={`p-3 rounded-md overflow-auto mb-4 ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-gray-200 text-gray-800'}`}>
          <code className={`font-mono text-sm ${className}`} {...props}>
            {children}
          </code>
        </pre>
      );
    },
    
    // Blockquotes with distinctive styling
    blockquote: ({node, ...props}) => (
      <blockquote className={`pl-4 border-l-4 ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'} italic my-4`} {...props} />
    ),
    
    // Tables with better styling
    table: ({node, ...props}) => (
      <div className="overflow-x-auto mb-4">
        <table className={`min-w-full ${darkMode ? 'border-gray-700' : 'border-gray-300'} border`} {...props} />
      </div>
    ),
    thead: ({node, ...props}) => <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'} {...props} />,
    tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700" {...props} />,
    tr: ({node, ...props}) => <tr className={darkMode ? 'border-gray-700' : 'border-gray-300'} {...props} />,
    th: ({node, ...props}) => <th className="px-4 py-2 text-left font-medium" {...props} />,
    td: ({node, ...props}) => <td className="px-4 py-2" {...props} />,
    
    // Strong and emphasis with proper styling
    strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
    em: ({node, ...props}) => <em className="italic" {...props} />,
    
    // Links with proper styling
    a: ({node, ...props}) => (
      <a 
        className={`text-blue-600 dark:text-blue-400 hover:underline`} 
        target="_blank" 
        rel="noopener noreferrer" 
        {...props} 
      />
    ),
    
    // Horizontal rule
    hr: ({node, ...props}) => <hr className={`my-6 ${darkMode ? 'border-gray-700' : 'border-gray-300'}`} {...props} />,
  };
  
  return (
    <div 
      className={`overflow-auto ${className}`}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <ReactMarkdown
        className={`prose max-w-none dark:prose-invert ${sizeClasses[size]}`}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={components}
      >
        {content.trim()}
      </ReactMarkdown>
    </div>
  );
};
